import { IUser } from 'store/models';
import { history } from 'helpers/history';
import { getTokenData } from 'helpers/decodeToken';
import { RoleEnumDto, UserInfoResponse } from '../.generated/api';
import { SessionStorageService } from 'services/sessionStorageService';
import { ApiService } from 'services/apiService';
import { clearEntraSession, isEntraSignedIn } from 'auth/msalConfig';
import { RelationItem } from 'store/models/UserAccess';
import { notificationActions } from './notificationActions';
import { ApplicationState } from 'store';
import { Action, Dispatch } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';

export const userActions = {
  login,
  loginWithEntra,
  logout,
  getUserInfo,
  getSawmillUserInfo,
};

function getUserInfo(): ThunkAction<void, ApplicationState, void, Action> {
  return (dispatch: ThunkDispatch<ApplicationState, void, Action>) => {
    const token = SessionStorageService.getToken();

    // A global admin has no explicit organization/department relations, so the
    // flag has to be read from the token when the state is rehydrated after a
    // full page reload (e.g. when a deep link is pasted into the address bar).
    if (token && getTokenData(token).isGlobalAdmin) {
      dispatch({
        type: 'SET_USER_INFO',
        userInfo: {
          organizations: [],
          departments: [],
          sawmills: [],
          isGlobalAdmin: true,
        },
      });
      return;
    }

    ApiService.getUserInfo()
      .then((resp) => {
        getUserAccessModel(resp.data).then((values) => {
          if (values) {
            dispatch({
              type: 'SET_USER_INFO',
              userInfo: {
                organizations: values[0],
                departments: values[1],
                sawmills: resp.data.sawmillRelations,
                isGlobalAdmin: false,
              },
            });
          }
        });
      })
      .catch((error) => {
        dispatch(notificationActions.enqueueErrorResponse(error));
      });
  };
}

function getSawmillUserInfo(): ThunkAction<
  void,
  ApplicationState,
  void,
  Action
> {
  return (dispatch: ThunkDispatch<ApplicationState, void, Action>) => {
    ApiService.getSawmillUserInfo().then((response) => {
      dispatch({
        type: 'SET_SAWMILL_USER_INFO',
        userInfo: response.data,
      });
    });
  };
}

function login(
  username: string,
  password: string,
  referrer?: string,
): ThunkAction<void, ApplicationState, void, Action> {
  return (dispatch: Dispatch<Action>): void => {
    dispatch(loginFailure()); // clear previous error
    ApiService.login(username, password)
      .then((response) => {
        completeLogin(
          dispatch,
          response.data,
          { username, password },
          referrer,
        );
      })
      .catch(() => {
        // TODO MAL: Check if error is result and use error information is available
        dispatch(loginFailure(username));
      });
  };
}

function loginWithEntra(
  idToken: string,
  referrer?: string,
): ThunkAction<Promise<void>, ApplicationState, void, Action> {
  return (dispatch: Dispatch<Action>): Promise<void> => {
    dispatch(loginFailure()); // clear previous error
    return ApiService.entraLogin(idToken)
      .then((response) => {
        const username = getTokenData(response.data.token).username;
        completeLogin(
          dispatch,
          response.data,
          { username, password: '' },
          referrer,
        );
      })
      .catch((err) => {
        const tenantRejected =
          err?.response?.data?.errorCode === 'entra_tenant_not_allowed';
        dispatch(
          loginFailure(tenantRejected ? 'MicrosoftTenant' : 'Microsoft'),
        );
      });
  };
}

function completeLogin(
  dispatch: Dispatch<Action>,
  tokenResponse: { token: string; refreshToken: string },
  user: IUser,
  referrer?: string,
): void {
  SessionStorageService.setToken(tokenResponse.token);
  SessionStorageService.setRefreshToken(tokenResponse.refreshToken);

  const tokenData = getTokenData(tokenResponse.token);

  if (tokenData.isGlobalAdmin) {
    dispatch({
      type: 'SET_USER_INFO',
      userInfo: {
        organizations: [],
        departments: [],
        isGlobalAdmin: true,
      },
    });

    dispatch(loginSuccess(user));

    history.push(referrer ?? '/organizations');
  } else {
    ApiService.getUserInfo().then((resp) => {
      getUserAccessModel(resp.data).then((values) => {
        if (values) {
          dispatch({
            type: 'SET_USER_INFO',
            userInfo: {
              organizations: values[0],
              departments: values[1],
              isGlobalAdmin: false,
              sawmills: resp.data.sawmillRelations,
            },
          });
          dispatch(loginSuccess(user));
          history.push(referrer ?? '/');
        }
      });
    });
  }
}

function loginSuccess(user: IUser) {
  return { type: 'LOGIN_SUCCESS', user };
}

function loginFailure(username?: string) {
  return { type: 'LOGIN_FAILURE', username };
}

function logout(): ThunkAction<void, ApplicationState, void, Action> {
  return (dispatch: Dispatch<Action>) => {
    SessionStorageService.clearToken();
    dispatch({ type: 'ORGANIZAION_RESET_STATE' });
    dispatch({ type: 'DEPARTMENT_STATE_RESET' });
    dispatch(success());
    if (isEntraSignedIn()) {
      // Clears the MSAL session and returns to the login page
      clearEntraSession();
      return;
    }
    history.push('/');
  };

  function success() {
    return { type: 'LOGOUT' };
  }
}

const getUserAccessModel = (resp: UserInfoResponse) => {
  return new Promise<Array<RelationItem[]>>((resolve) => {
    Promise.all([
      getOrganizations(resp.organizationRelations),
      getDepartments(resp.departmentRelations),
    ]).then((values) => {
      resolve(values);
    });
  });
};

const getDepartments = (departmentRelations: RelationItem[]) => {
  return new Promise<RelationItem[]>((resolve) => {
    const departments: {
      id: string;
      organizationId: string;
      roles: RoleEnumDto[];
    }[] = [];
    let orgId: string;
    if (
      departmentRelations.filter((relation) =>
        relation.roles.includes(RoleEnumDto.DEPARTMENT_ADMIN),
      ).length > 0
    ) {
      ApiService.getDepartmentList().then((resp) => {
        departmentRelations
          .filter((relation) =>
            relation.roles.includes(RoleEnumDto.DEPARTMENT_ADMIN),
          )
          .forEach((relation, index) => {
            orgId = resp?.data?.data?.filter(
              (department) => department.id === relation.id,
            )[0]?.organizationId;

            departments.push({
              id: relation.id,
              organizationId: orgId,
              roles: relation.roles,
            });
            if (
              index ===
              departmentRelations.filter((relation) =>
                relation.roles.includes(RoleEnumDto.DEPARTMENT_ADMIN),
              ).length -
                1
            ) {
              resolve(departments);
            }
          });
      });
    } else {
      resolve(departments);
    }
  });
};

export const getOrganizations = (organizationRelations: RelationItem[]) => {
  return new Promise<RelationItem[]>((resolve) => {
    const organizations: {
      id: string;
      roles: RoleEnumDto[];
    }[] = [];
    organizationRelations?.forEach((orgRole) => {
      if (
        orgRole.roles.includes(RoleEnumDto.ORGANIZATION_ADMIN) ||
        orgRole.roles.includes(RoleEnumDto.SUPERVISOR)
      ) {
        organizations.push({
          id: orgRole.id,
          roles: orgRole.roles,
        });
      }
    });
    resolve(organizations);
  });
};
